import { Field, ID, ObjectType } from 'type-graphql'
import {
  Column,
  CreateDateColumn,
  Entity,
  Index,
  ManyToOne,
  PrimaryGeneratedColumn,
  RelationId,
  UpdateDateColumn
} from 'typeorm'

import { Domain, roundTransformer } from '@things-factory/shell'
import { Appliance, User } from '@things-factory/auth-base'
import { Scenario } from '@things-factory/integration-base'

import { DataSet } from '../data-set/data-set.js'

@Entity()
@Index('ix_data_sensor_0', (dataSensor: DataSensor) => [dataSensor.domain, dataSensor.name], { unique: true })
@Index('ix_data_sensor_1', (dataSensor: DataSensor) => [dataSensor.deviceId], { unique: true })
@Index('ix_data_sensor_2', (dataSensor: DataSensor) => [dataSensor.domain, dataSensor.refBy], {
  unique: false
})
@Index('ix_data_sensor_3', (dataSensor: DataSensor) => [dataSensor.domain, dataSensor.serialNo], {
  unique: false
})
@ObjectType({
  description:
    'Represents a data sensor configuration that connects hardware devices to datasets for automated data collection. This entity manages the communication protocol and data mapping.'
})
export class DataSensor {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for the data sensor.' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field(type => Domain)
  domain?: Domain

  @RelationId((dataSensor: DataSensor) => dataSensor.domain)
  domainId?: string

  @Column()
  @Field({ description: 'Name of the data sensor for identification.' })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Description of the data sensor configuration.' })
  description?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  active?: boolean

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Unique identifier of the hardware device connected to this sensor.' })
  deviceId?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  serialNo?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Brand name of the hardware device.' })
  brand?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Model name of the hardware device.' })
  model?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  netmask?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  tag?: string

  @Column({ type: 'float', nullable: true, comment: '용량', transformer: roundTransformer })
  @Field({ nullable: true, description: 'Capacity or measurement range of the sensor.' })
  capacity?: number

  @Column({ nullable: true, comment: '용량 단위' })
  @Field({ nullable: true, description: 'Unit of measurement for the sensor capacity.' })
  unit?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  refBy?: string

  @ManyToOne(type => Appliance, { nullable: true })
  @Field({ nullable: true })
  appliance?: Appliance

  @RelationId((dataSensor: DataSensor) => dataSensor.appliance)
  applianceId?: string

  @ManyToOne(type => DataSet, { nullable: true })
  @Field({ nullable: true })
  dataSet?: DataSet

  @RelationId((dataSensor: DataSensor) => dataSensor.dataSet)
  dataSetId?: string

  @ManyToOne(type => Scenario, { nullable: true })
  @Field(type => Scenario, {
    nullable: true,
    description: 'The decoder scenario that automatically executes based on the trigger of raw data collection'
  })
  decoder?: Scenario

  @RelationId((dataSensor: DataSensor) => dataSensor.decoder)
  decoderId?: string

  @CreateDateColumn()
  @Field({ nullable: true })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  creator?: User

  @RelationId((dataSensor: DataSensor) => dataSensor.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  updater?: User

  @RelationId((dataSensor: DataSensor) => dataSensor.updater)
  updaterId?: string
}
